import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import i18n from './i18n.js';

const LANGUAGE_PREFERENCE_STORAGE_KEY = 'ibe.language.preference';

/**
 * Fired synchronously on `window` when the guest explicitly selects a
 * language. Consumers that need transient user activation (e.g. the Web
 * Translator API model download) must handle this event, not the context
 * state update, so the call still counts as user-activated.
 */
export const LANGUAGE_SELECTED_EVENT = 'ibe:language-selected';

/**
 * Guest-facing language options.
 *
 * These codes mirror the active rows in the `public.languages` table.
 * English remains the baseline fallback.
 */
export const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'العربية' },
  { value: 'de', label: 'Deutsch' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'hi', label: 'हिन्दी' },
  { value: 'id', label: 'Bahasa Indonesia' },
  { value: 'it', label: 'Italiano' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'ms', label: 'Bahasa Melayu' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'pt', label: 'Português' },
  { value: 'th', label: 'ไทย' },
  { value: 'vi', label: 'Tiếng Việt' },
  { value: 'zh', label: '中文' },
] as const;

const SUPPORTED_LANGUAGE_CODES: readonly string[] = LANGUAGE_OPTIONS.map((option) => option.value);

/** Fallback language while site/tenant default language is not wired. */
const FALLBACK_LANGUAGE = 'en';

/** Locales whose document direction is right-to-left. */
const RTL_LANGUAGES: ReadonlySet<string> = new Set(['ar']);

export interface LanguageContextValue {
  readonly language: string;
  readonly setLanguage: (language: string) => void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function isSupportedLanguage(language: string): boolean {
  return SUPPORTED_LANGUAGE_CODES.includes(language);
}

function resolveLanguagePreference(stored: string | null): string {
  if (stored !== null && isSupportedLanguage(stored)) return stored;
  return FALLBACK_LANGUAGE;
}

function readLanguagePreference(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(LANGUAGE_PREFERENCE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeLanguagePreference(language: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LANGUAGE_PREFERENCE_STORAGE_KEY, language);
  } catch {
    // localStorage can be disabled or full; the in-memory preference still works.
  }
}

export function LanguageProvider({
  children,
}: {
  readonly children: ReactNode;
}): React.JSX.Element {
  const [language, setLanguageState] = useState<string>(() =>
    resolveLanguagePreference(readLanguagePreference()),
  );

  useEffect(() => {
    writeLanguagePreference(language);
    if (i18n.language !== language) void i18n.changeLanguage(language);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
      document.documentElement.dir = RTL_LANGUAGES.has(language) ? 'rtl' : 'ltr';
    }
  }, [language]);

  const setLanguage = useCallback((next: string) => {
    const resolved = resolveLanguagePreference(next);
    setLanguageState(resolved);
    void i18n.changeLanguage(resolved);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(LANGUAGE_SELECTED_EVENT, { detail: resolved }));
    }
  }, []);

  const value = useMemo<LanguageContextValue>(
    () => ({ language, setLanguage }),
    [language, setLanguage],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (context === null) {
    return { language: FALLBACK_LANGUAGE, setLanguage: () => undefined };
  }
  return context;
}
